import {compose, ComposerFn} from './utils/compose'
import {Optional} from '@othree.io/optional'
import {RemovalPolicy, Stack} from 'aws-cdk-lib'
import {LogGroup} from 'aws-cdk-lib/aws-logs'
import {Effect, ManagedPolicy, PolicyStatement, Role, ServicePrincipal} from 'aws-cdk-lib/aws-iam'
import {
    AddRoutesOptions,
    ApiMapping,
    CfnStage,
    CorsHttpMethod,
    HttpApi,
    HttpApiProps,
    HttpMethod,
    HttpRoute,
    HttpRouteKey,
    IHttpApi,
    IHttpRoute,
    IHttpRouteAuthorizer,
    DomainName
} from 'aws-cdk-lib/aws-apigatewayv2'
import {IFunction} from 'aws-cdk-lib/aws-lambda'
import {HttpJwtAuthorizer} from 'aws-cdk-lib/aws-apigatewayv2-authorizers'
import {HttpLambdaIntegration} from 'aws-cdk-lib/aws-apigatewayv2-integrations'
import {Cors} from 'aws-cdk-lib/aws-apigateway'

const DEFAULT_STAGE = 'release'

export type HttpApiDomain = Readonly<{
    domain: string
    basePath: string
}>

export type HttpApiInput = Readonly<{
    apiName: string
}>

export type HttpApiConfiguration = Partial<HttpApiProps> & Readonly<{
    domain?: HttpApiDomain
}>

/** Returns a default HTTP API configuration with CORS allowing all origins and GET requests. */
export const withDefaultApiConfiguration = (): HttpApiConfiguration => {
    return {
        corsPreflight: {
            allowCredentials: false,
            allowMethods: [CorsHttpMethod.GET],
            allowOrigins: Cors.ALL_ORIGINS,
            allowHeaders: Cors.DEFAULT_HEADERS
        }
    }
}

/** Builds an HTTP API configuration with a JWT authorizer (e.g. Cognito, Auth0). */
export const withAuthorizerConfiguration = (name: string, issuer: string, audience: Array<string>): HttpApiConfiguration => {
    return {
        defaultAuthorizer: new HttpJwtAuthorizer(name, issuer, {
            authorizerName: name,
            jwtAudience: audience
        })
    }
}

/**
 * Creates an HTTP API (API Gateway V2) with a custom stage, JSON access logging,
 * and optional custom domain mapping.
 */
export const withHttpApi = (deps: {
    defaultConfiguration: HttpApiConfiguration
    stack: Stack
}) =>
    (input: HttpApiInput, configuration?: Partial<HttpApiProps>): IHttpApi => {
        const httpApi = new HttpApi(deps.stack, input.apiName, {
            apiName: input.apiName,
            createDefaultStage: false,
            ...deps.defaultConfiguration,
            ...Optional(configuration!).orElse({})
        })

        const logGroup = new LogGroup(deps.stack, `${input.apiName}-LogGroup`, {
            logGroupName: `${httpApi.httpApiName}-${DEFAULT_STAGE}`,
            removalPolicy: RemovalPolicy.DESTROY
        })

        const role = new Role(deps.stack, `${input.apiName}-Role`, {
            assumedBy: new ServicePrincipal('apigateway.amazonaws.com'),
        })

        const policy = new ManagedPolicy(deps.stack, `${input.apiName}-Policy`, {
            statements: [
                new PolicyStatement({
                    effect: Effect.ALLOW,
                    actions: [
                        'logs:CreateLogGroup',
                        'logs:CreateLogStream',
                        'logs:PutLogEvents'
                    ],
                    resources: ['*']
                })
            ],
            roles: [role]
        })

        const cfnStage = new CfnStage(deps.stack, `${input.apiName}-Stage`, {
            apiId: httpApi.apiId,
            accessPolicyId: policy.managedPolicyArn,
            stageName: DEFAULT_STAGE,
            autoDeploy: true,
            defaultRouteSettings: {
                detailedMetricsEnabled: true,
                loggingLevel: 'info'
            },
            accessLogSettings: {
                destinationArn: logGroup.logGroupArn,
                format: JSON.stringify({
                    requestId: '$context.requestId',
                    ip: '$context.identity.sourceIp',
                    requestTime: '$context.requestTime',
                    httpMethod: '$context.httpMethod',
                    routeKey: '$context.routeKey',
                    status: '$context.status',
                    protocol: '$context.protocol',
                    responseLength: '$context.responseLength',
                    userAgent: '$context.identity.userAgent',
                    integrationLatency: '$context.integrationLatency',
                    sub: '$context.authorizer.claims.sub',
                    responseLatency: '$context.responseLatency'
                })
            }
        })

        Optional(deps.defaultConfiguration.domain!).map(domainConfig => {
            const domain = DomainName.fromDomainNameAttributes(deps.stack, `${input.apiName}-Domain`, {
                name: domainConfig.domain
            } as any)

            new ApiMapping(deps.stack, `${domain.name}-${domainConfig.basePath}`, {
                api: httpApi,
                domainName: domain,
                stage: cfnStage as any,
                apiMappingKey: domainConfig.basePath,
            })
        })

        return httpApi
    }

export type HttpRouteConfiguration = Readonly<{
    method: HttpMethod
    fn: IFunction
    options?: Partial<AddRoutesOptions>
}>

/** Adds a Lambda-backed route to a composed HTTP API. */
export const withRoute = (path: string, configuration: HttpRouteConfiguration) =>
    (api: ComposedHttpApi): ComposedHttpApi => {
        const lambdaIntegration = new HttpLambdaIntegration(`${path}-Fn`, configuration.fn)

        const httpRoute = new HttpRoute(api.api.stack, `${configuration.method}${path}`, {
            httpApi: api.api,
            routeKey: HttpRouteKey.with(path, configuration.method),
            integration: lambdaIntegration,
            authorizer: api.defaultHttpOptions.authorizer,
            ...Optional(configuration.options).orElse({})
        })

        const route = {
            [path]: httpRoute
        }

        return {
            routes: {
                ...api.routes,
                ...route
            },
            defaultHttpOptions: api.defaultHttpOptions,
            api: api.api
        }
    }

export type DefaultHttpApiOptions = Readonly<{
    authorizer?: IHttpRouteAuthorizer
}>

/** An HTTP API paired with accumulated routes and default options, threaded through composition. */
export type ComposedHttpApi = Readonly<{
    api: IHttpApi
    defaultHttpOptions: DefaultHttpApiOptions
    routes: {
        [key: string]: IHttpRoute
    }
}>

/** Composes an HTTP API by piping it through a sequence of `withRoute` calls. */
export const composedHttpApi = (deps: {
    defaultOptions: DefaultHttpApiOptions
}) =>
    (api: IHttpApi, ...args: Array<ComposerFn<ComposedHttpApi>>): ComposedHttpApi => {
        const composedRestApi: ComposedHttpApi = {
            api: api,
            defaultHttpOptions: deps.defaultOptions,
            routes: {}
        }

        return compose(composedRestApi, ...args)
    }
